import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { apiRequest } from '../../../utils/fetch';
import { CouponCodeInterface, GenericResponseInterface } from '../../../interfaces/interfaces';

const initialCode: Partial<CouponCodeInterface> = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 0,
  maxUsage: 100,
  usageCount: 0,
  validFrom: new Date(),
  validUntil: new Date(),
  createdByUserId: 1, // Admin user ID
  adSpaceId: null,
  platformSubsidized: false,
  isGlobal: true,
  autoApply: false,
  active: true,
};

const CouponCodesAdminAdd: React.FC = () => {
  const [code, setCode] = useState<Partial<CouponCodeInterface>>(initialCode);
  const [validFrom, setValidFrom] = useState<Dayjs | null>(dayjs());
  const [validUntil, setValidUntil] = useState<Dayjs | null>(dayjs().add(30, 'day'));
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleChange = (field: keyof CouponCodeInterface, value: any) => {
    setCode(c => ({ ...c, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...code,
        validFrom: validFrom?.toISOString(),
        validUntil: validUntil?.toISOString(),
      };

      const result = await apiRequest<GenericResponseInterface<CouponCodeInterface>>('/couponCodes/insert', 'POST', payload);
      if (result.status) {
        setSuccess(result.message || 'Global coupon code created successfully!');
        setDialogOpen(true);
        setCode(initialCode);
        setValidFrom(dayjs());
        setValidUntil(dayjs().add(30, 'day'));
      } else {
        setError(result.message || 'Failed to create coupon code');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create coupon code');
      setDialogOpen(true);
    }
  };

  const discountTypeHelperText = code.discountType === 'percentage' 
    ? 'Enter 10 for 10% discount' 
    : 'Enter actual amount (e.g., 30 for $30 off)';

  return (
    <Paper sx={{ p: 3, maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New Global Coupon Code</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Code"
          value={code.code ?? ''}
          onChange={e => handleChange('code', e.target.value.toUpperCase())}
          fullWidth
          margin="dense"
          required
          helperText="Discount code (will be converted to uppercase)"
        />

        <FormControl fullWidth margin="dense">
          <InputLabel id="discount-type-label">Discount Type</InputLabel>
          <Select
            labelId="discount-type-label"
            value={code.discountType ?? 'percentage'}
            label="Discount Type"
            onChange={e => handleChange('discountType', e.target.value)}
          >
            <MenuItem value="percentage">Percentage</MenuItem>
            <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Discount Value"
          type="number"
          value={code.discountValue ?? 0}
          onChange={e => handleChange('discountValue', parseFloat(e.target.value))}
          fullWidth
          margin="dense"
          required
          helperText={discountTypeHelperText}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <DatePicker
              label="Valid From"
              value={validFrom}
              onChange={(newValue) => setValidFrom(newValue)}
              slotProps={{ textField: { fullWidth: true, margin: 'dense', required: true } }}
            />
            <DatePicker
              label="Valid Until"
              value={validUntil}
              onChange={(newValue) => setValidUntil(newValue)}
              slotProps={{ textField: { fullWidth: true, margin: 'dense', required: true } }}
            />
          </Box>
        </LocalizationProvider>

        <TextField
          label="Max Usage"
          type="number"
          value={code.maxUsage ?? 100}
          onChange={e => handleChange('maxUsage', parseInt(e.target.value))}
          fullWidth
          margin="dense"
          required
          helperText="Total number of times this code can be used"
        />

        <TextField
          label="Description (optional)"
          value={code.description ?? ''}
          onChange={e => handleChange('description', e.target.value)}
          fullWidth
          margin="dense"
          multiline
          rows={2}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="is-global-label">Is Global</InputLabel>
            <Select
              labelId="is-global-label"
              value={code.isGlobal !== undefined ? (code.isGlobal ? 'yes' : 'no') : 'yes'}
              label="Is Global"
              onChange={e => handleChange('isGlobal', e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel id="auto-apply-label">Auto Apply</InputLabel>
            <Select
              labelId="auto-apply-label"
              value={code.autoApply !== undefined ? (code.autoApply ? 'yes' : 'no') : 'no'}
              label="Auto Apply"
              onChange={e => handleChange('autoApply', e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="platform-subsidized-label">Platform Subsidized</InputLabel>
            <Select
              labelId="platform-subsidized-label"
              value={code.platformSubsidized !== undefined ? (code.platformSubsidized ? 'yes' : 'no') : 'no'}
              label="Platform Subsidized"
              onChange={e => handleChange('platformSubsidized', e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel id="active-label">Active</InputLabel>
            <Select
              labelId="active-label"
              value={code.active !== undefined ? (code.active ? 'yes' : 'no') : 'yes'}
              label="Active"
              onChange={e => handleChange('active', e.target.value === 'yes')}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create Coupon Code
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CouponCodesAdminAdd;
